// Web Push on the phone (docs/protocol.md, "Push"; SPEC section 4). The app subscribes with the
// relay's VAPID key and registers the subscription over the authenticated WebSocket. The service
// worker (src/sw/sw.ts) does the showing.
import { fromB64u, type ClientFrame } from "@whistle/protocol";
import type { PushHealth } from "./model";
import type { FetchLike } from "./pairing";

export const SW_URL = "/sw.js";

/** What to say when /sw.js won't register. Reinstalling never helps: in dev the server isn't serving it (vite.sw.ts). */
export const NO_SW_TEXT = import.meta.env.DEV
  ? "Alerts need the service worker, and this server didn't send one. Use the preview build: npm run preview:usb."
  : "The app's background worker didn't load. Reload the page, then try again.";

export function pushSupported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    return reg;
  } catch {
    return null;
  }
}

/** What the Connections screen shows about alerts, read fresh each time (permission can change in settings). */
export async function readPushHealth(registered = false): Promise<PushHealth> {
  if (!pushSupported()) return { supported: false, permission: "default", subscribed: false, registered: false };
  const permission = Notification.permission === "granted" ? "granted" : Notification.permission === "denied" ? "denied" : "default";
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    subscribed = !!(await reg?.pushManager.getSubscription());
  } catch {
    subscribed = false;
  }
  return { supported: true, permission, subscribed, registered };
}

export async function fetchVapidKey(relayOrigin: string, fetchImpl: FetchLike = fetch): Promise<string> {
  const res = await fetchImpl(`${relayOrigin.replace(/\/+$/, "")}/v1/vapid`, { credentials: "omit", cache: "no-store" });
  if (!res.ok) throw new Error(`relay answered ${res.status}`);
  const body = (await res.json()) as { publicKey?: string };
  if (!body.publicKey) throw new Error("no publicKey");
  return body.publicKey;
}

export type EnablePushDeps = {
  relayOrigin: string;
  sendFrame: (frame: ClientFrame) => boolean;
  fetchImpl?: FetchLike;
};

export type EnablePushFailure = "denied" | "dismissed" | "unsupported" | "no-sw" | "relay" | "subscribe" | "not-connected";

export type EnablePushResult = { ok: true; health: PushHealth } | { ok: false; health: PushHealth; why: EnablePushFailure };

/**
 * What to tell the user when alerts didn't turn on, with the fix. Pairing and Connections both show this, so the
 * same failure always reads the same. `lastError` is the browser's or relay's own words, added where it helps.
 */
export function enablePushFailureText(why: EnablePushFailure, lastError?: string): string {
  const detail = lastError ? ` (${shortError(lastError)})` : "";
  switch (why) {
    case "denied":
      return "The browser blocked notifications. Allow them in the site settings, then try again.";
    case "dismissed":
      return "The browser's question was closed without an answer. Tap Turn on alerts again and choose Allow.";
    case "unsupported":
      return "This browser can't receive push alerts. Chrome or Samsung Internet on Android can.";
    case "no-sw":
      return NO_SW_TEXT;
    case "relay":
      return `Couldn't get the push key from the relay${detail}. Try again in a moment.`;
    case "not-connected":
      return "Subscribed, but not connected to the relay yet. It registers as soon as the connection is back.";
    case "subscribe":
      return `Subscribing failed${detail}. Try again, and if it keeps failing, reinstall the app from the browser menu.`;
  }
}

function shortError(text: string): string {
  // eslint-disable-next-line no-control-regex
  const clean = text.replace(/[\u0000-\u001f\u007f-\u009f]/g, " ").replace(/\s+/g, " ").trim();
  return clean.length > 120 ? `${clean.slice(0, 119)}…` : clean;
}

/** Ask for permission, subscribe, and register with the relay. Each failure names itself so the UI can give the fix. */
export async function enablePush(deps: EnablePushDeps): Promise<EnablePushResult> {
  if (!pushSupported()) return { ok: false, why: "unsupported", health: await readPushHealth() };
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, why: permission === "denied" ? "denied" : "dismissed", health: await readPushHealth() };
  const reg = (await navigator.serviceWorker.getRegistration("/")) ?? (await registerServiceWorker());
  if (!reg) return { ok: false, why: "no-sw", health: await readPushHealth() };
  await navigator.serviceWorker.ready;
  let key: string;
  try {
    key = await fetchVapidKey(deps.relayOrigin, deps.fetchImpl);
  } catch (e) {
    return { ok: false, why: "relay", health: { ...(await readPushHealth()), lastError: e instanceof Error ? e.message : String(e) } };
  }
  let sub: PushSubscription | null = null;
  try {
    sub = await reg.pushManager.getSubscription();
    if (sub && !sameKey(sub, key)) {
      await sub.unsubscribe();
      sub = null;
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toArrayBuffer(fromB64u(key)) });
  } catch (e) {
    return { ok: false, why: "subscribe", health: { ...(await readPushHealth()), lastError: e instanceof Error ? e.message : String(e) } };
  }
  const sent = registerSubscription(sub, deps.sendFrame);
  const health = await readPushHealth(sent);
  return sent ? { ok: true, health } : { ok: false, why: "not-connected", health };
}

/** Send the current subscription to the relay; done on every connect so a rotated subscription is never lost. */
export function registerSubscription(sub: PushSubscription, sendFrame: (frame: ClientFrame) => boolean): boolean {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return false;
  return sendFrame({ t: "registerPush", subscription: { endpoint: json.endpoint, expirationTime: json.expirationTime ?? null, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } } });
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    return (await reg?.pushManager.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

function sameKey(sub: PushSubscription, keyB64u: string): boolean {
  const have = sub.options?.applicationServerKey;
  if (!have) return true;
  const a = new Uint8Array(have);
  const b = fromB64u(keyB64u);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

function toArrayBuffer(b: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(b.byteLength);
  new Uint8Array(out).set(b);
  return out;
}
